import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

import IncomingMessage from './IncomingMessage';
import OutgoingMessage from './OutgoingMessage';
import UploadDocuments from '../../modals/UploadDocuments';
import ChatMicButton from './shared/ChatMicButton';
import { useAppDispatch } from '../../../store/hooks';
import { NUMBERS } from '../../../resources/number-constants';
import { removeNullValues, getValue } from '../../../utility/helpers';
import { getChats, uploadDocuments } from '../../../store/features/dashboard';
import { climateLogo, proceedArrow, chatPlaneIcon, paperclipIcon } from '../../../assets/images/images';
import { UserCompanyDetails, ChatMessage, useFetchUserDetails } from '../../../utility/chatUtils';
import { useChatSpeechInput } from '../../../hooks/useChatSpeechInput';

const ChatWithAI = ({
    conversationId,
    generateJobDescription,
    handleSaveJob
}: any) => {
    const dispatch = useAppDispatch();
    const messageRef = useRef<HTMLTextAreaElement>(null);
    const [chatList, setChatList] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lastMessageId, setLastMessageId] = useState<number | null>(null);
    const [userCompanyDetails, setUserCompanyDetails] = useState<UserCompanyDetails>({ logo: '', userId: '' });
    const [enableGenerateResponse, setEnableGenerateResponse] = useState(false);
    const [showSendButton, setShowSendButton] = useState(false);
    const [inputMessage, setInputMessage] = useState<string | null>(null);
    const [lastQuestionStep, setLastQuestionStep] = useState<string | null>(null);
    const [showUploadDocModal, setShowUploadDocModal] = useState(false);

    const chatContainerRef = useRef<HTMLDivElement>(null); // Ref for chat container

    const { micStatus, toggleRecording, shouldBlockSend } = useChatSpeechInput({
        messageRef,
        setInputMessage,
        setShowSendButton,
    });

    // Scroll to the bottom of chat
    const scrollToBottom = () => {
        const el = chatContainerRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
        requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
            requestAnimationFrame(() => {
                el.scrollTop = el.scrollHeight;
            });
        });
    };

    // Memoize requestPayload to avoid recreation on every render
    const requestPayload = useCallback((options: any) => {
        const body: any = {
            step: lastQuestionStep,
            payload: {
                title: inputMessage ? null : options,
                message: inputMessage ? options : null,
            },
            is_multi_select: Array.isArray(options) && options?.length > 1,
            conversation_id: conversationId,
            payload_type: NUMBERS.THREE,
            user: userCompanyDetails?.userId,
        };

        // Only include parent_id if it's a valid backend ID (not a temporary local ID)
        const isValidParentId = !!lastMessageId &&
            typeof lastMessageId === 'number' &&
            Array.isArray(chatList) &&
            chatList.some((m: any) => m?.id === lastMessageId && !m?.is_local);
        if (isValidParentId) {
            body.parent_id = lastMessageId;
        }

        return body;
    }, [lastMessageId, conversationId, userCompanyDetails, lastQuestionStep, inputMessage, chatList]);

    // Fetch chat details with loader handling
    const fetchChatDetails = useCallback(async () => {
        try {
            const payload = { conversation_id: conversationId, limit: 300 };
            const res = await dispatch(getChats(payload)).unwrap();
            setChatList(res?.data);
        } catch (error) {
            console.info('Error during fetching chat details:', error);
        } finally {
            setIsLoading(false); // Hide loader
        }
    }, [dispatch, conversationId]);

    const fetchUserDetails = useFetchUserDetails();

    // Send message handler with optimistic update
    const handleSendMessage = async (options: any, initialAnswer: any) => {
        if (messageRef.current) {
            messageRef.current.value = '';
            messageRef.current.style.height = '56px';
        }

        const payload = requestPayload(options);
        const newMessage = {
            id: `temp_${Date.now()}`,
            ...payload
        };

        // Optimistically update chat list
        setChatList((prevChatList: any) => [...prevChatList, newMessage]);
        setShowSendButton(false);
        try {
            setIsLoading(true);
            await dispatch(uploadDocuments(removeNullValues(payload))).unwrap();
            setInputMessage(null);
            await fetchChatDetails(); // Refresh chat list after successful API response
        } catch (error) {
            console.info('Error sending message:', error);
            setIsLoading(false);
            // Optionally handle reverting optimistic update on error
        }
    };

    const send = (event: any) => {
        event.preventDefault();

        const messageInput = messageRef.current;

        if (messageInput) {
            const trimmedMessage = messageInput.value.trim();

            if (shouldBlockSend(trimmedMessage)) {
                return;
            }

            if (trimmedMessage) {
                setShowSendButton(true);
                handleSendMessage(trimmedMessage, null);
                messageInput.value = '';
                messageInput.style.height = '56px';
            }
        }
    };

    // Input change handler for trimming spaces and showing send button
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const trimmedInput = e.target.value.trim(); // Trim input in real-time

        // Update the state only if the message contains any non-space characters
        setInputMessage(trimmedInput.length > 0 ? e.target.value : null);
        if (trimmedInput.length > 0) {
            setShowSendButton(true);
        } else {
            setShowSendButton(false);
        }
    };

    const handleEnterKeyPress = (event: any) => {
        if ((event.key === 'Enter') && !event.shiftKey) {
            event.preventDefault();
            send(event);
        } else if ((event.key === 'Enter') && event.shiftKey) {
            event.stopPropagation();
        }
    };

    const adjustTextareaSizeAndNotifyTyping = (event: any) => {
        const { value, scrollHeight } = event?.target;
        const isEmpty = value === '' || value === '\n';

        if (isEmpty) {
            event.target.style.height = '56px';
        } else {
            event.target.style.height = `${scrollHeight}px`;
        }
    };

    const renderTooltip = (props: any) => (
        <Tooltip id="button-tooltip" {...props}>
            {enableGenerateResponse ? 'You can now able to generate job' : 'Please fulfill the details for required job description result'}
        </Tooltip>
    );

    const renderAttachmentTooltip = (props: any) => (
        <Tooltip id="attachment-tooltip" {...props}>
            Upload job description, call transcript, or URL link
        </Tooltip>
    );

    const handleGenerate = () => {
        if (!enableGenerateResponse) {
            return;
        }
        generateJobDescription();
    };

    // Scroll to bottom when chat list is updated or loader changes
    useEffect(() => {
        scrollToBottom();
    }, [chatList, isLoading]);

    // Enable Generate when Hunter has posted a finish summary, even if is_finished is missing.
    useEffect(() => {
        const finished = Array.isArray(chatList) && chatList.some((c: any) =>
            c?.is_finished ||
            c?.result?.step === 'finish' ||
            c?.step === 'finish'
        );
        if (finished) {
            setEnableGenerateResponse(true);
        }
    }, [chatList]);

    useEffect(() => {
        if (conversationId) {
            fetchChatDetails();
            fetchUserDetails().then(setUserCompanyDetails);
        }
    }, [conversationId, fetchChatDetails, fetchUserDetails]);

    // Add beforeunload event listener to save conversation when user navigates away
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            // Save the conversation before the user leaves the page
            if (handleSaveJob && conversationId) {
                handleSaveJob();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [handleSaveJob, conversationId]);
    return (
        <>
            <div className="container">
                <div className="client-chat-main chat-with-ai">
                    <div className="messaging">
                        <div className="inbox_msg">
                            <div className="mesgs" ref={chatContainerRef}>
                                <div className="msg_history">
                                    <div className="chat-ai-profile-wrap">
                                        <img src={climateLogo} alt="profile" className="chat-ai-profile-img" />
                                        <h1 className="chat-profile-head">
                                            Introducing Hunter
                                        </h1>
                                        <span className="chat-profile-des righteous-head">your personal AI Recruiting Assistant<span className="green-dot">.</span></span>
                                    </div>

                                    {chatList?.length > 0 && chatList.map((chat: any, index: number) => (
                                        <div key={chat?.id || chat?.timestamp} className='incoming-outgoing-wrap'>
                                            {(chat?.payload_type !== NUMBERS.FOUR) && (
                                                <OutgoingMessage
                                                    chat={chat}
                                                    companyLogo={userCompanyDetails?.logo}
                                                />
                                            )}
                                            {(chat?.payload_type === NUMBERS.FOUR) && (
                                                <IncomingMessage
                                                    chat={chat}
                                                    isLoading={isLoading}
                                                    setEnableGenerateResponse={setEnableGenerateResponse}
                                                    message={chat?.result}
                                                    setLastMessageId={setLastMessageId}
                                                    handleSendMessage={handleSendMessage}
                                                    setLastQuestionStep={setLastQuestionStep}
                                                    componentType="general"
                                                    isLastMessage={index === chatList.length - 1}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    {isLoading && (
                                        <div className="wrapper bounce-loader-wrapper">
                                            <div className="circle"></div>
                                            <div className="circle"></div>
                                            <div className="circle"></div>
                                        </div>
                                    )}
                                </div>

                                <div className="textarea-chat-row">
                                    <div className={`textarea-wrap ${showSendButton ? 'has-send' : ''}`}>
                                        <OverlayTrigger
                                            placement="top"
                                            delay={{ show: 250, hide: 400 }}
                                            overlay={renderAttachmentTooltip}
                                        >
                                            <img
                                                src={paperclipIcon}
                                                alt="attachment"
                                                className="chat-attachment-btn"
                                                onClick={() => setShowUploadDocModal(true)}
                                            />
                                        </OverlayTrigger>
                                        <ChatMicButton
                                            status={micStatus}
                                            onClick={toggleRecording}
                                            disabled={isLoading}
                                        />
                                        <textarea
                                            className={`form-control ${micStatus === 'transcribing' ? 'is-transcribing' : ''}`}
                                            id="exampleFormControlTextarea1"
                                            rows={3}
                                            onInput={adjustTextareaSizeAndNotifyTyping}
                                            placeholder="Type here to ask anything"
                                            onChange={handleInputChange}
                                            ref={messageRef}
                                            onKeyDown={(e) => handleEnterKeyPress(e)}
                                            readOnly={micStatus === 'transcribing'}
                                        >
                                        </textarea>
                                        <img
                                            src={chatPlaneIcon}
                                            alt="send"
                                            className={`chat-send-btn ${showSendButton ? '' : 'd-none'}`} // Show/hide button based on input
                                            onClick={() => {
                                                if (shouldBlockSend(inputMessage || undefined)) return;
                                                handleSendMessage(inputMessage, null);
                                            }} // Send message on click
                                        />
                                    </div>
                                    <OverlayTrigger
                                        placement="top"
                                        delay={{ show: 250, hide: 400 }}
                                        overlay={renderTooltip}
                                    >
                                        <Button
                                            className={`generate-btn ${!enableGenerateResponse ? 'disable' : ''}`}
                                            onClick={handleGenerate}
                                        >
                                            Generate
                                            <span className='btn-right-img'><img src={proceedArrow} alt="generate" /></span>
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showUploadDocModal && (
                <UploadDocuments
                    userId={getValue('userId')}
                    showUploadDocModal={showUploadDocModal}
                    setShowUploadDocModal={setShowUploadDocModal}
                />
            )}
        </>
    );
};

export default ChatWithAI;
